import { create } from 'zustand'

// Type for signMessage function from wallet adapter
type SignMessageFn = ((message: Uint8Array) => Promise<Uint8Array>) | null
type DisconnectFn = (() => Promise<void>) | null

interface WalletState {
  // Connection state
  connected: boolean
  connecting: boolean
  publicKey: string | null

  // Auth state
  isAuthenticated: boolean
  authToken: string | null
  authExpiry: number | null

  // Wallet functions (set by provider)
  signMessage: SignMessageFn
  disconnect: DisconnectFn

  // Actions
  setConnected: (connected: boolean) => void
  setConnecting: (connecting: boolean) => void
  setPublicKey: (publicKey: string | null) => void
  setSignMessage: (fn: SignMessageFn) => void
  setDisconnect: (fn: DisconnectFn) => void
  setAuth: (token: string, expiry: number) => void
  clearAuth: () => void
  fullDisconnect: () => Promise<void>
}

export const useWalletStore = create<WalletState>((set, get) => ({
  // Initial state
  connected: false,
  connecting: false,
  publicKey: null,
  isAuthenticated: false,
  authToken: null,
  authExpiry: null,
  signMessage: null,
  disconnect: null,

  // Setters
  setConnected: (connected) => set({ connected }),
  setConnecting: (connecting) => set({ connecting }),
  setPublicKey: (publicKey) => set({ publicKey, connected: !!publicKey }),
  setSignMessage: (signMessage) => set({ signMessage }),
  setDisconnect: (disconnect) => set({ disconnect }),

  setAuth: (token, expiry) =>
    set({
      isAuthenticated: true,
      authToken: token,
      authExpiry: expiry,
    }),

  clearAuth: () =>
    set({
      isAuthenticated: false,
      authToken: null,
      authExpiry: null,
    }),

  // Full disconnect - clears wallet and auth
  fullDisconnect: async () => {
    const { disconnect, clearAuth } = get()
    clearAuth()
    if (disconnect) {
      await disconnect()
    }
    set({
      connected: false,
      publicKey: null,
    })
  },
}))

// Selector hooks
export const useIsWalletConnected = () => useWalletStore((state) => state.connected)
export const useWalletPublicKey = () => useWalletStore((state) => state.publicKey)
export const useIsAuthenticated = () => useWalletStore((state) => state.isAuthenticated)
export const useWalletSignMessage = () => useWalletStore((state) => state.signMessage)
